import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Wrench, Trash2, Sparkles, Pencil } from "lucide-react";
import type { MaintenanceAnalysis } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { AttachmentField } from "@/components/AttachmentField";
import { AiAnalysisPanel } from "@/components/AiAnalysisPanel";
import { QueryError } from "@/components/QueryError";

type Status = "OPEN" | "ASSIGNED" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";
type Priority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";
type Category = "PLUMBING" | "ELECTRICAL" | "STRUCTURAL" | "COMMON_AREA" | "ELEVATOR" | "SECURITY" | "OTHER";

interface MaintenanceRequest {
  id: string;
  unitId?: string | null;
  category: Category;
  title: string;
  description: string;
  priority: Priority;
  status: Status;
  photoUrls: string[];
  resolutionNotes?: string | null;
  enteredOnBehalf?: boolean;
  aiAnalysis?: MaintenanceAnalysis | null;
  createdAt: string;
  resolvedAt?: string | null;
  unit?: { unitNumber: string } | null;
  reportedById: string;
  reportedBy: { firstName: string; lastName: string };
  assignedTo?: { firstName: string; lastName: string } | null;
}

const statusVariant: Record<Status, "default" | "info" | "warning" | "success"> = {
  OPEN: "warning",
  ASSIGNED: "info",
  IN_PROGRESS: "info",
  RESOLVED: "success",
  CLOSED: "default",
};

const priorityVariant: Record<Priority, "default" | "info" | "warning" | "danger"> = {
  LOW: "default",
  MEDIUM: "info",
  HIGH: "warning",
  URGENT: "danger",
};

const STATUS_FLOW: Status[] = ["OPEN", "ASSIGNED", "IN_PROGRESS", "RESOLVED", "CLOSED"];

export function MaintenancePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editFor, setEditFor] = useState<MaintenanceRequest | null>(null);

  const { data: requests = [], isLoading, isError } = useQuery({
    queryKey: ["maintenance-requests"],
    queryFn: async () => (await api.get<MaintenanceRequest[]>("/maintenance-requests")).data,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, resolutionNotes }: { id: string; status: Status; resolutionNotes?: string }) =>
      (await api.put(`/maintenance-requests/${id}`, { status, resolutionNotes })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance-requests"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/maintenance-requests/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance-requests"] }),
  });

  const analyzeMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/maintenance-requests/${id}/analyze`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance-requests"] }),
  });

  const canManage = user?.role === "ADMIN" || user?.role === "BOARD_MEMBER";
  const isAdmin = user?.role === "ADMIN";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wrench size={24} /> {t("maintenance.title")}
        </h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("maintenance.submit")}
        </button>
      </div>

      {isError ? (
        <QueryError />
      ) : isLoading ? (
        <div className="text-slate-500">{t("common.loading")}</div>
      ) : requests.length === 0 ? (
        <div className="card text-slate-500">—</div>
      ) : (
        <div className="space-y-4">
          {requests.map((r) => {
            // The administration and the board correct any request; the reporter, their own
            // while nobody has started on it.
            const canEditThis = canManage || (r.reportedById === user?.id && r.status === "OPEN");
            return (
            <div key={r.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant={priorityVariant[r.priority]}>{t(`maintenance.prio.${r.priority}`)}</Badge>
                    <Badge variant={statusVariant[r.status]}>{t(`maintenance.status.${r.status}`)}</Badge>
                    <Badge>{t(`maintenance.cat.${r.category}`)}</Badge>
                    {r.unit && <span className="text-xs text-slate-500">· Unidad {r.unit.unitNumber}</span>}
                    {r.enteredOnBehalf && <Badge variant="default">{t("worksPermits.onBehalf")}</Badge>}
                  </div>
                  <h3 className="text-lg font-semibold">{r.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{r.description}</p>
                  <div className="text-xs text-slate-500 mt-2">
                    Reportado por {r.reportedBy.firstName} {r.reportedBy.lastName} · {formatDate(r.createdAt, i18n.language)}
                    {r.assignedTo && <span> · Asignado a {r.assignedTo.firstName} {r.assignedTo.lastName}</span>}
                  </div>
                  {r.photoUrls.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {r.photoUrls.map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-block">
                          <img src={u} alt="" className="w-16 h-16 object-cover rounded border border-slate-200" />
                        </a>
                      ))}
                    </div>
                  )}
                  {r.resolutionNotes && (
                    <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                      <strong>Resolución:</strong> {r.resolutionNotes}
                      {r.resolvedAt && <span className="text-xs text-slate-500"> · {formatDate(r.resolvedAt, i18n.language)}</span>}
                    </div>
                  )}
                </div>
                {(canManage || isAdmin || canEditThis) && (
                  <div className="shrink-0 flex flex-col items-end gap-2">
                    {canEditThis && (
                      <button className="btn-secondary text-xs" onClick={() => setEditFor(r)}>
                        <Pencil size={12} /> {t("common.edit")}
                      </button>
                    )}
                    {canManage && (
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => analyzeMutation.mutate(r.id)}
                        disabled={analyzeMutation.isPending && analyzeMutation.variables === r.id}
                      >
                        <Sparkles size={12} />
                        {analyzeMutation.isPending && analyzeMutation.variables === r.id
                          ? t("maintenance.analyzing")
                          : r.aiAnalysis ? t("maintenance.reanalyze") : t("maintenance.analyze")}
                      </button>
                    )}
                    {canManage && r.status !== "CLOSED" && (
                      <select
                        className="input text-xs"
                        value={r.status}
                        onChange={(e) => {
                          const newStatus = e.target.value as Status;
                          if (newStatus === "RESOLVED") {
                            const notes = prompt(t("maintenance.resolutionNotes") + ":");
                            updateStatusMutation.mutate({ id: r.id, status: newStatus, resolutionNotes: notes ?? undefined });
                          } else {
                            updateStatusMutation.mutate({ id: r.id, status: newStatus });
                          }
                        }}
                      >
                        {STATUS_FLOW.map((s) => (
                          <option key={s} value={s}>{t(`maintenance.status.${s}`)}</option>
                        ))}
                      </select>
                    )}
                    {isAdmin && (
                      <button
                        className="text-red-600 hover:text-red-700 text-xs inline-flex items-center gap-1"
                        title={t("common.delete")}
                        onClick={() => { if (confirm(t("maintenance.confirmDelete"))) deleteMutation.mutate(r.id); }}
                      >
                        <Trash2 size={14} /> {t("common.delete")}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {canManage && r.aiAnalysis && (
                <div className="mt-3">
                  <AiAnalysisPanel analysis={r.aiAnalysis as MaintenanceAnalysis} defaultOpen={false} />
                </div>
              )}

              <Timeline status={r.status} resolvedAt={r.resolvedAt} createdAt={r.createdAt} />
            </div>
            );
          })}
        </div>
      )}

      <RequestFormModal
        open={open || !!editFor}
        target={editFor}
        onClose={() => {
          setOpen(false);
          setEditFor(null);
        }}
        onDone={() => qc.invalidateQueries({ queryKey: ["maintenance-requests"] })}
      />
    </div>
  );
}

function Timeline({ status, createdAt, resolvedAt }: { status: Status; createdAt: string; resolvedAt?: string | null }) {
  const idx = STATUS_FLOW.indexOf(status);
  return (
    <div className="flex items-center gap-1 mt-4">
      {STATUS_FLOW.map((s, i) => (
        <div key={s} className="flex-1 flex items-center">
          <div
            className={`h-1.5 flex-1 rounded ${i <= idx ? "bg-primary-500" : "bg-slate-200"}`}
            title={s}
          />
        </div>
      ))}
    </div>
  );
}

const emptyForm = () => ({ category: "OTHER" as Category, title: "", description: "", priority: "MEDIUM" as Priority });

/**
 * Create a request (no `target`) or correct one (`target` given): one form for both, so a
 * wrong title, description, category, unit or photo is fixed in place.
 */
function RequestFormModal({
  open,
  target,
  onClose,
  onDone,
}: {
  open: boolean;
  target?: MaintenanceRequest | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm());
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);

  const canOnBehalf =
    user?.role === "ADMIN" || (user?.permissions?.includes("onbehalf:maintenance") ?? false);
  // Priority is workflow, set by the administration; a resident only picks it when reporting.
  const canManage = user?.role === "ADMIN" || user?.role === "BOARD_MEMBER";
  const showPriority = !target || canManage;

  useEffect(() => {
    if (!open) return;
    if (target) {
      setForm({ category: target.category, title: target.title, description: target.description, priority: target.priority });
      setSelectedUnitId(target.unitId ?? "");
      setPhotoUrls(target.photoUrls ?? []);
    } else {
      setForm(emptyForm());
      setSelectedUnitId("");
      setPhotoUrls([]);
    }
  }, [open, target]);

  const { data: units = [] } = useQuery({
    queryKey: ["units-picker"],
    queryFn: async () => (await api.get<{ id: string; unitNumber: string }[]>("/units")).data,
    enabled: open && canOnBehalf,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (target) {
        const body = {
          category: form.category,
          title: form.title,
          description: form.description,
          photoUrls: photoUrls.filter(Boolean),
          ...(canManage ? { priority: form.priority } : {}),
          ...(canOnBehalf ? { unitId: selectedUnitId || null } : {}),
        };
        return (await api.put(`/maintenance-requests/${target.id}`, body)).data;
      }
      return (await api.post("/maintenance-requests", {
        ...form,
        unitId: user?.unitId ?? (selectedUnitId || null),
        photoUrls: photoUrls.filter(Boolean),
      })).data;
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={target ? t("maintenance.editRequest") : t("maintenance.submit")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        {(target ? canOnBehalf : !user?.unitId && canOnBehalf) && (
          <div>
            <label className="label">{t("worksPermits.unit")}</label>
            <select className="input" value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)}>
              <option value="">{t("maintenance.cat.COMMON_AREA")}</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>{u.unitNumber}</option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("maintenance.category")}</label>
            <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as Category })}>
              {(["PLUMBING", "ELECTRICAL", "STRUCTURAL", "COMMON_AREA", "ELEVATOR", "SECURITY", "OTHER"] as Category[]).map((c) => (
                <option key={c} value={c}>{t(`maintenance.cat.${c}`)}</option>
              ))}
            </select>
          </div>
          {showPriority && (
          <div>
            <label className="label">{t("maintenance.priority")}</label>
            <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}>
              {(["LOW", "MEDIUM", "HIGH", "URGENT"] as Priority[]).map((p) => (
                <option key={p} value={p}>{t(`maintenance.prio.${p}`)}</option>
              ))}
            </select>
          </div>
          )}
        </div>
        <div>
          <label className="label">{t("maintenance.title2")}</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("maintenance.description")}</label>
          <textarea className="input min-h-[100px]" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <AttachmentField
          label={t("maintenance.photos")}
          folder="costadorada1/maintenance"
          accept=".jpg,.jpeg,.png,.heic,.webp,.pdf"
          addLabel={t("maintenance.addPhoto")}
          urls={photoUrls}
          onChange={setPhotoUrls}
        />
        {mutation.isError && <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
